import { useState } from 'preact/hooks';
import type { OnsiteProduct, OnsitePrice, CheckoutText, PromoDiscount } from '@/types';
import { CheckoutShell } from '@/components/CheckoutShell';
import { getPortalBase } from '@/api/client';
import { openSubscriberPortal } from '@/portalHandoff';
import { isPaywallPriceVisible } from '@/page';
import { enFlat } from '@/i18n/en';
import { t, useT } from '@/i18n/t';
import { cadenceLabel, intervalNoun } from '@/i18n/interval';
import { formatMinorUnits, isZeroDecimal, majorToMinor, minorToMajorText } from '@/money';
import { pickerPromoLabel, trialDiscountFromPromo } from '@/components/promoDescriptor';
import { discountSpan } from '@/components/OrderSummary';

// handleManageSubscriptionClick makes the built-in "Manage subscription" link
// hand off seamlessly for an ezAuth-verified member (the portal tab lands signed
// in) while keeping its href as the fallback. It bows out for the dashboard
// preview (where the link is an inline-edit target the preview wires its own
// click on — see widget/src/preview.tsx) and for modified clicks (new
// tab/window, middle-click), letting the anchor's plain navigation to the bare
// portal handle those.
function handleManageSubscriptionClick(event: MouseEvent): void {
  const el = event.currentTarget as HTMLElement;
  if (el.dataset.smWired) {
    return;
  }
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  event.preventDefault();
  openSubscriberPortal();
}

interface Props {
  products: OnsiteProduct[];
  // Publisher-authored per-domain overrides of the paywall chrome copy (trust
  // line, "cancel anytime", "manage subscription"). Each string falls back to
  // the widget default independently when unset.
  checkoutText?: CheckoutText;
  // The item supplied to showPaywall({ product, item }). Opts runtime
  // item-scoped prices into the paywall; page-sourced prices show regardless.
  item?: string;
  // Server-masked checkout identity (ezAuth only), set when the picker is shown
  // after a Back from payment so the visitor knows which identity a
  // re-selection will use. Unset on a fresh paywall.
  maskedEmail?: string;
  // Renders a "Not you?" switch next to the identity note. Absent hides it
  // (guests, and BYO domains whose adapter has no logout).
  onSwitchIdentity?: () => void;
  onSelectPrice: (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => void;
  // Selected for a trial-eligible price's "Start N-day free trial" CTA. Absent
  // (e.g. donations) falls back to the normal subscribe/buy action.
  onStartTrial?: (product: OnsiteProduct, price: OnsitePrice) => void;
  onLogin: () => void;
  // The externalId of the currently-highlighted price, controlling which card
  // reads as selected and which price the single "Continue" CTA acts on. Set
  // together with onSelectPriceUid to control selection from a parent (the
  // dashboard preview, which must route selection through its own re-render so
  // it never re-renders internally with its injected chrome present — see
  // widget/src/preview.tsx). Left unset live, where the paywall owns the
  // selection with its own state, seeded to the first offered price.
  selectedPriceUid?: string;
  onSelectPriceUid?: (uid: string) => void;
  // Set when the surface can be dismissed (the donation flow, or a paywall opened
  // with dismissible: true); a blocking page gate omits it.
  onDismiss?: () => void;
  // Publisher-requested preset amount (cents) for a variable donation; threaded
  // through to the amount picker, which ignores it when out of range.
  initialAmountCents?: number;
  // The campaign code's per-price discounts (the config's promo block, keyed by
  // price externalId), gated by the flow on the code still being live — a card
  // with an entry renders struck-through original + discounted amount. Absent
  // for donations and uncoded mounts.
  promoDiscounts?: Record<string, PromoDiscount>;
  // The campaign code itself, for the "code applied" line under the list.
  // Shown only when at least one price carries a discount.
  promoCode?: string;
  // Set only by the dashboard preview bundle. Keeps the donate button clickable
  // even before a valid amount is chosen, so its inline ctaLabel edit target
  // isn't a disabled control (which swallows clicks). Never set on live sites,
  // where the button correctly disables until the visitor picks a valid amount.
  editable?: boolean;
}

function formatMoney(amountCents: number, currency: string): string {
  return formatMinorUnits(amountCents, currency);
}

function formatAmount(price: OnsitePrice): string {
  return formatMoney(price.amountCents, price.currency);
}

function intervalLabel(price: OnsitePrice): string | null {
  // Only recurring prices carry a meaningful billing interval; one-time and
  // donation prices ship an inert interval (e.g. "none") that must not render as
  // "per none".
  if (price.type !== 'recurring' || !price.billingInterval) return null;
  return cadenceLabel(price.billingInterval, price.billingIntervalCount ?? 1);
}

const RECURRING_PRICE_LABEL_KEYS: Record<string, string> = {
  day: 'priceLabel.daily',
  week: 'priceLabel.weekly',
  month: 'priceLabel.monthly',
  year: 'priceLabel.yearly',
};

// The default per-price label when a recurring or one-time price carries no
// publisher name, derived from its cadence so a blank label still reads sensibly
// on the paywall (and gives the preview a node to click and edit). Donation and
// unknown-type prices return "" (no synthesized label, matching prior behavior).
// Exported so the builder preview can resolve the same default and normalize an
// edit equal to it back to a blank override.
export function defaultPriceLabel(price: OnsitePrice): string {
  if (price.type === 'recurring' && price.billingInterval) {
    const count = price.billingIntervalCount ?? 1;
    if (count === 1) {
      const labelKey = RECURRING_PRICE_LABEL_KEYS[price.billingInterval];
      if (labelKey) {
        return t(labelKey);
      }
      return t('priceLabel.everyInterval', { interval: intervalNoun(price.billingInterval, 1) });
    }
    return t('priceLabel.everyCount', { n: count, count, interval: intervalNoun(price.billingInterval, count) });
  }
  if (price.type === 'one_time') {
    return t('priceLabel.oneTime');
  }
  return '';
}

// The default purchase-button text when a price carries no ctaLabel override.
// When the button is leading with the trial CTA (`trial`), it reads "Start N-day
// free trial" with the day count kept dynamic; otherwise it resolves from the
// product/price type. `trial` is the caller's render-time decision (a price can
// be trial-eligible yet still fall back to the standard action when no trial
// handler is wired). Exported so the builder preview can show it as the
// placeholder and normalize an edit equal to it back to a blank override.
export function defaultCtaLabel(product: OnsiteProduct, price: OnsitePrice, trial: boolean): string {
  if (trial) {
    const days = trialDays(price);
    return t('cta.startTrial', { n: days, days });
  }
  if (product.type === 'donation') {
    return t('cta.donate');
  }
  if (price.type === 'recurring') {
    return t('cta.subscribe');
  }
  if (price.type === 'one_time') {
    return t('cta.buy');
  }
  return t('cta.continue');
}

// The default donate-button label. Like the trial badge's {days}, it carries an
// {amount} token the widget fills with the visitor's chosen amount; the publisher
// edits this template in the dashboard preview, repositioning or dropping the
// token. Exported so the preview shows it as the placeholder and normalizes an
// unchanged edit back to a blank override.
export const DEFAULT_DONATION_CTA_LABEL = enFlat('donation.ctaDefault');

// donateButtonLabel resolves the donate button's text: the publisher's ctaLabel
// template (or the default), with every {amount} token replaced by the formatted
// amount. A template with no token renders verbatim, so the publisher can drop
// the amount from the button entirely.
function donateButtonLabel(price: OnsitePrice, amountCents: number): string {
  const template = price.ctaLabel || t('donation.ctaDefault');
  return template.replace(/\{amount\}/g, formatMoney(amountCents, price.currency));
}

// The default free-trial badge template. A publisher's trialBadgeLabel override
// replaces it; both use a "{days}" token the widget fills with the resolved
// trial length. Exported so the builder preview can offer the raw template
// (with its literal token) for inline editing and normalize an unchanged edit
// back to a blank override.
export const DEFAULT_TRIAL_BADGE_LABEL = enFlat('trialBadge.default');

export function trialBadgeTemplate(override?: string): string {
  return override || t('trialBadge.default');
}

// Default checkout/paywall chrome copy — the last-resort strings rendered when
// neither the product override nor the domain default is set. Exported so the
// builder preview can normalize an edit that matches the inherited value back to
// a blank (inherit) override, mirroring the CTA/trial-badge defaults above.
export const DEFAULT_SECURE_CHECKOUT_NOTE = enFlat('trust.secureCheckout');
export const DEFAULT_CANCEL_ANYTIME_LABEL = enFlat('trust.cancelAnytime');
export const DEFAULT_MANAGE_SUBSCRIPTION_LABEL = enFlat('trust.manageSubscription');

// TrialBadge renders the resolved trial template, substituting the price's trial
// length for the first "{days}" token. The pill and the count span both carry
// data-sm-edit anchors so the dashboard preview can open its trial editor
// popover from either (the popover edits length and wording together; data-sm-*
// are inert on live sites). With the default template this yields the same
// visible DOM as before: `<span data-sm-edit>{days}</span>-day free trial`. A
// template with no token renders as static text — the day count still lives in
// the popover's length field, so it is never stranded when the wording omits it.
function TrialBadge({ days, uid, template }: { days: number; uid: string; template: string }) {
  const token = '{days}';
  const idx = template.indexOf(token);
  if (idx === -1) {
    return <span class="sm-plan-badge" data-sm-edit="trialBadgeLabel" data-sm-uid={uid}>{template}</span>;
  }
  const before = template.slice(0, idx);
  const after = template.slice(idx + token.length).replace(/\{days\}/g, String(days));
  // Wrap the text + count in one inline box. .sm-plan-badge is inline-flex, so
  // rendering before/count/after as direct children makes each a flex item and
  // flexbox strips the leading/trailing space of a text-run item — dropping the
  // space in e.g. "Free trial ({days} days)". Keeping them in a single child
  // preserves the wording exactly without changing the pill's layout.
  return (
    <span class="sm-plan-badge" data-sm-edit="trialBadgeLabel" data-sm-uid={uid}>
      <span class="sm-plan-badge-content">
        {before}
        <span class="sm-plan-badge-days" data-sm-edit="trialPeriodDays" data-sm-uid={uid}>{days}</span>
        {after}
      </span>
    </span>
  );
}

// PromoBadge renders the publisher's marketing badge text (e.g. "Recommended").
// It carries the data-sm-edit anchor so the dashboard preview can edit the text
// inline via the same text seam as the price name/CTA (inert on live sites).
function PromoBadge({ label, uid }: { label: string; uid: string }) {
  return <span class="sm-plan-promo-badge" data-sm-edit="badgeLabel" data-sm-uid={uid}>{label}</span>;
}

// trialDays returns the offered free-trial length for a price, or 0 when it has
// no trial or the resolving visitor is no longer eligible. Exported so the
// builder preview can derive the same trial state the widget renders (the
// preview always wires a trial handler, so a positive count means a trial CTA).
export function trialDays(price: OnsitePrice): number {
  if (!price.trialEligible || !price.trialPeriodDays || price.trialPeriodDays <= 0) {
    return 0;
  }
  return price.trialPeriodDays;
}

function paywallSubtitle(hasRecurring: boolean, hasOneTime: boolean): string {
  if (hasRecurring && hasOneTime) {
    return t('paywall.subtitle.mixed');
  }
  if (hasOneTime) {
    return t('paywall.subtitle.oneTime');
  }
  return t('paywall.subtitle.subscribe');
}

// The idle screen renders a lock-framed wall with one card per product. No order
// summary or step indicator here — those belong to the checkout steps that
// follow once a visitor picks a price. A donation (opened via openDonation or a
// [data-ezoic-donate] trigger, never a paywall) gets its own support framing
// instead of the paywall copy.
export function PaywallOverlay(props: Props) {
  const { products, checkoutText, onSelectPrice, onDismiss, initialAmountCents, maskedEmail, onSwitchIdentity, editable } = props;
  const isDonation = products.length > 0 && products.every((p) => p.type === 'donation');
  // Dispatch to the donation gate as a distinct subtree so the paywall body's
  // selection hook is never mounted on a donation product (and vice versa) —
  // keeping each component's hook order stable across a product-type change.
  if (isDonation) {
    return <DonationGate products={products} checkoutText={checkoutText} onSelectPrice={onSelectPrice} onDismiss={onDismiss} initialAmountCents={initialAmountCents} maskedEmail={maskedEmail} onSwitchIdentity={onSwitchIdentity} editable={editable} />;
  }
  return <Paywall {...props} />;
}

// The subscription/one-time paywall body: a single-select list of price cards
// with one shared "Continue" CTA that acts on the highlighted card. Split out
// from the dispatcher above so its selection hook always runs (never behind the
// donation early return).
function Paywall({ products, checkoutText, item, maskedEmail, onSwitchIdentity, onSelectPrice, onStartTrial, onLogin, onDismiss, selectedPriceUid, onSelectPriceUid, promoDiscounts, promoCode }: Props) {
  useT();
  // A runtime item-scoped price needs an item to be sellable, so it is hidden
  // from a bare paywall; page-sourced and product/recurring prices always show.
  // Filter once here so the wall chrome (title/subtitle) and the cards agree on
  // which prices are on offer, and drop products left with nothing to sell.
  const paywallProducts = products
    .filter((p) => p.type !== 'donation')
    .map((product) => ({ product, prices: product.prices.filter((price) => isPaywallPriceVisible(price, item)) }))
    .filter((entry) => entry.prices.length > 0);
  const hasRecurring = paywallProducts.some((entry) => entry.prices.some((price) => price.type === 'recurring'));
  const hasOneTime = paywallProducts.some((entry) => entry.prices.some((price) => price.type === 'one_time'));

  // The visitor picks one price across every product, so flatten the cards into
  // a single selectable list and highlight one. Selection is controlled when the
  // parent supplies it (the preview); otherwise the paywall owns it, seeded to
  // the publisher's preselected price when one is flagged, else the first
  // offered price so the CTA is live without a click. `selected` falls
  // back to the first entry if the highlighted uid ever goes stale (e.g. a price
  // filtered out), so the CTA never points at a missing price.
  const entries = paywallProducts.flatMap(({ product, prices }) => prices.map((price) => ({ product, price })));
  const [internalUid, setInternalUid] = useState(entries.find((entry) => entry.price.preselected)?.price.externalId ?? entries[0]?.price.externalId ?? '');
  const selectedUid = selectedPriceUid ?? internalUid;
  const selected = entries.find((entry) => entry.price.externalId === selectedUid) ?? entries[0];
  const selectUid = (uid: string) => {
    if (onSelectPriceUid) {
      onSelectPriceUid(uid);
      return;
    }
    setInternalUid(uid);
  };
  // The shared CTA leads with the trial action only when the selected price is
  // trial-eligible and a trial handler is wired (mirrors the badge gating).
  const selectedTrial = !!selected && trialDays(selected.price) > 0 && !!onStartTrial;

  // A paywall resolves a single product, so its per-product override (when set)
  // owns the header framing; each string falls back to the computed default
  // independently (an empty override field is treated as unset). Read it off the
  // walled product itself, not the price-filtered list, so the publisher's
  // header copy survives even when a product's prices are all filtered out.
  const walled = products.find((p) => p.type !== 'donation');
  const framing = walled?.paywallText;
  const eyebrow = framing?.eyebrow || t('paywall.eyebrow');
  const title = framing?.title || (hasRecurring ? t('paywall.title.subscribe') : t('paywall.title.unlock'));
  const subtitle = framing?.subtitle || paywallSubtitle(hasRecurring, hasOneTime);
  const loginPrompt = hasRecurring ? t('paywall.login.subscribed') : t('paywall.login.purchased');
  // Checkout chrome copy cascades per string: product override, then domain
  // default, then the computed default (mirrors the header framing above).
  const manageSubscriptionLabel = walled?.checkoutText?.manageSubscriptionLabel || checkoutText?.manageSubscriptionLabel || t('trust.manageSubscription');

  return (
    <CheckoutShell ariaLabel={title} currentStep={1} hideSteps onDismiss={onDismiss}>
      <div class="sm-eyebrow sm-eyebrow-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {/* data-sm-edit tags the dashboard preview's inline-edit targets. Inert on
            live sites — only the preview bundle wires them (see widget/src/preview.tsx). */}
        <span class="sm-eyebrow-text" data-sm-edit="eyebrow">{eyebrow}</span>
      </div>
      <h2 class="sm-title" data-sm-edit="title">{title}</h2>
      <p class="sm-subtitle" data-sm-edit="subtitle">{subtitle}</p>
      {maskedEmail && (
        <p class="sm-identity-note">
          {t('paywall.purchasingAs')} <strong>{maskedEmail}</strong>
          {onSwitchIdentity && (
            <>
              {' · '}
              <button type="button" class="sm-identity-change" onClick={onSwitchIdentity}>{t('paywall.notYou')}</button>
            </>
          )}
        </p>
      )}

      {/* The campaign code's confirmation, once, above the list: it frames the
          prices before the visitor reads them, and keeps its success-green
          band clear of the (often green) CTA below the list. Only when at
          least one price actually discounts. */}
      {promoCode && promoDiscounts && Object.keys(promoDiscounts).length > 0 && (
        <p class="sm-plan-promo-applied">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.15 14.35-4.1-4.1 1.4-1.4 2.7 2.7 5.7-5.7 1.4 1.4z" />
          </svg>
          <PromoAppliedText code={promoCode} label={campaignLabel(paywallProducts, promoDiscounts)} />
        </p>
      )}

      <div class="sm-plan-list" role="radiogroup" aria-label={title}>
        {paywallProducts.map(({ product, prices }) => (
          <ProductCard key={product.externalId} product={product} prices={prices} onStartTrial={onStartTrial} selectedUid={selected?.price.externalId} onSelectUid={selectUid} promoDiscounts={promoDiscounts} />
        ))}
      </div>

      {/* One shared CTA for the whole list, acting on the highlighted card. The
          inert data-sm-edit="ctaLabel" anchor rides the selected price so the
          preview edits that price's label; the override wins for both the trial
          and standard label, blank falling back to the type-derived default. */}
      {selected && (
        <div class="sm-plan-cta">
          <button
            type="button"
            class="sm-btn sm-btn-primary sm-plan-choose"
            data-sm-edit="ctaLabel"
            data-sm-uid={selected.price.externalId}
            onClick={() => (selectedTrial ? onStartTrial!(selected.product, selected.price) : onSelectPrice(selected.product, selected.price))}
          >
            {selected.price.ctaLabel || defaultCtaLabel(selected.product, selected.price, selectedTrial)}
          </button>
        </div>
      )}

      <PaywallTrust products={products} checkoutText={checkoutText} />

      {/* Account chrome: a hairline rule ends the offer, then the returning-user
          actions share one centered line (log in on-site, or manage an existing
          subscription in the portal). The shell appends the support line below. */}
      <div class="sm-paywall-account">
        <button class="sm-account-link" type="button" onClick={onLogin}>
          {loginPrompt}
        </button>
        {hasRecurring && (
          <span class="sm-account-manage">
            <span class="sm-account-sep" aria-hidden="true">·</span>
            <a class="sm-account-link" href={getPortalBase()} target="_blank" rel="noopener noreferrer" data-sm-edit="manageSubscriptionLabel" onClick={handleManageSubscriptionClick}>
              {manageSubscriptionLabel}
            </a>
          </span>
        )}
      </div>
    </CheckoutShell>
  );
}

interface DonationGateProps {
  products: OnsiteProduct[];
  checkoutText?: CheckoutText;
  onSelectPrice: (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => void;
  onDismiss?: () => void;
  initialAmountCents?: number;
  // Same back-from-payment identity note as the paywall (see Props.maskedEmail).
  maskedEmail?: string;
  // Same "Not you?" switch as the paywall (see Props.onSwitchIdentity).
  onSwitchIdentity?: () => void;
  editable?: boolean;
}

function DonationGate({ products, checkoutText, onSelectPrice, onDismiss, initialAmountCents, maskedEmail, onSwitchIdentity, editable }: DonationGateProps) {
  useT();
  const lead = products[0];
  // The donation's header framing: the eyebrow falls back to "Support" (a
  // publisher can override it via paywallText.eyebrow, like the paywall's
  // "Members only"); the title/subtitle ARE the donation's name/description, so
  // they carry the product-level data-sm-edit anchors and the ProductCard below
  // suppresses its own name/desc to avoid rendering them twice.
  const eyebrow = lead.paywallText?.eyebrow || t('donation.eyebrow');

  return (
    <CheckoutShell ariaLabel={lead.name} currentStep={1} hideSteps onDismiss={onDismiss}>
      <div class="sm-eyebrow sm-eyebrow-icon">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 21s-7.5-4.9-10-9.3C.6 9 1.6 5.7 4.5 5.1c1.9-.4 3.6.5 4.6 1.9 1-1.4 2.7-2.3 4.6-1.9 2.9.6 3.9 3.9 2.5 6.6C19.5 16.1 12 21 12 21z" />
        </svg>
        {/* data-sm-edit tags the dashboard preview's inline-edit targets. Inert on
            live sites — only the preview bundle wires them (see widget/src/preview.tsx). */}
        <span class="sm-eyebrow-text" data-sm-edit="eyebrow">{eyebrow}</span>
      </div>
      <h2 class="sm-title" data-sm-edit="name">{lead.name}</h2>
      {lead.description && <p class="sm-subtitle" data-sm-edit="description">{lead.description}</p>}
      {maskedEmail && (
        <p class="sm-identity-note">
          {t('paywall.purchasingAs')} <strong>{maskedEmail}</strong>
          {onSwitchIdentity && (
            <>
              {' · '}
              <button type="button" class="sm-identity-change" onClick={onSwitchIdentity}>{t('paywall.notYou')}</button>
            </>
          )}
        </p>
      )}

      <div class="sm-plan-list">
        {products.map((product) => (
          <ProductCard key={product.externalId} product={product} onSelectPrice={onSelectPrice} initialAmountCents={initialAmountCents} editable={editable} />
        ))}
      </div>

      <PaywallTrust products={products} checkoutText={checkoutText} />
    </CheckoutShell>
  );
}

interface ProductCardProps {
  product: OnsiteProduct;
  // The prices to render. The paywall passes its scope/source-filtered subset;
  // the donation gate omits it and falls back to the product's own prices.
  prices?: OnsitePrice[];
  // Only the donation gate wires this, for the variable-amount picker's own
  // submit; paywall price cards defer their action to the shared CTA instead.
  onSelectPrice?: (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => void;
  onStartTrial?: (product: OnsiteProduct, price: OnsitePrice) => void;
  // The highlighted price across the whole list, and the setter a card calls
  // when clicked. Wired by the paywall; unset on the donation gate (whose cards
  // are amount pickers, not selectable price rows).
  selectedUid?: string;
  onSelectUid?: (uid: string) => void;
  // Only consumed by the variable-donation picker; harmless for other prices.
  initialAmountCents?: number;
  editable?: boolean;
  // Campaign discounts keyed by price externalId (see Props.promoDiscounts).
  promoDiscounts?: Record<string, PromoDiscount>;
}

function ProductCard({ product, prices: pricesProp, onSelectPrice, onStartTrial, selectedUid, onSelectUid, initialAmountCents, editable, promoDiscounts }: ProductCardProps) {
  const prices = pricesProp ?? product.prices ?? [];
  // A donation renders inside the gate, whose header already shows the donation's
  // name/description (as the editable title/subtitle). Repeating them on the card
  // would double the copy and the "name"/"description" edit anchors, so the card
  // shows only the amount picker for donations.
  const isDonation = product.type === 'donation';

  return (
    <div class="sm-plan">
      {/* data-sm-edit marks a preview inline-edit target; per-price nodes below
          also carry data-sm-uid so the dashboard maps the edit to the right
          price. Inert on live sites (only the preview bundle wires them). */}
      {!isDonation && <h3 class="sm-plan-name" data-sm-edit="name">{product.name}</h3>}
      {!isDonation && product.description && <p class="sm-plan-desc" data-sm-edit="description">{product.description}</p>}

      {prices.map((price) => (
        price.type === 'donation_variable'
          ? <DonationAmountPicker key={price.externalId} product={product} price={price} onSelectPrice={onSelectPrice!} initialAmountCents={initialAmountCents} editable={editable} />
          : <PriceOption
              key={price.externalId}
              price={price}
              trial={trialDays(price) > 0 && !!onStartTrial}
              selected={selectedUid === price.externalId}
              onSelect={() => onSelectUid?.(price.externalId)}
              discount={promoDiscounts?.[price.externalId]}
            />
      ))}
    </div>
  );
}

// campaignLabel builds the deal label for the code-applied line from any
// discounted price (a code's amount and duration are code-level; per-price
// entries only differ by eligibility).
function campaignLabel(entries: { prices: OnsitePrice[] }[], discounts: Record<string, PromoDiscount>): string | undefined {
  for (const entry of entries) {
    for (const price of entry.prices) {
      const discount = discounts[price.externalId];
      if (discount) {
        return pickerPromoLabel(price, discount);
      }
    }
  }
  return undefined;
}

// PromoAppliedText renders the "{code} applied · {label}" line with the code
// in a notranslate span: the template splits around the {code} token so
// locale word order holds (the TrialBadge pattern). The label is the payment
// step chip's copy (promo.chip.*).
function PromoAppliedText({ code, label }: { code: string; label?: string }) {
  const [before, after] = t('paywall.promoApplied').split('{code}');
  return (
    <span>
      {before}
      <span class="sm-plan-promo-applied-code notranslate">{code}</span>
      {after}
      {label && <span class="sm-plan-promo-applied-label notranslate"> · {label}</span>}
    </span>
  );
}

interface PriceOptionProps {
  price: OnsitePrice;
  // Whether this card leads with the trial badge — the paywall decides, gating
  // on both trial eligibility and a wired trial handler.
  trial: boolean;
  selected: boolean;
  onSelect: () => void;
  // The campaign code's discount for this price (the config promo block):
  // renders the struck-through original + the server-computed discounted
  // amount. Absent renders the plain price.
  discount?: PromoDiscount;
  // Upgrade picker only: blocks selection while a quote loads.
  disabled?: boolean;
}

export function PriceOption({ price, trial, selected, onSelect, discount, disabled }: PriceOptionProps) {
  useT();
  const interval = intervalLabel(price);
  const span = discount && price.type === 'recurring' ? discountSpan(price, trialDiscountFromPromo(discount)) : null;
  // A first-charge-only discounted amount isn't recurring.
  const showInterval = !!interval && span !== 'first';
  const days = trialDays(price);
  // Fall back to a cadence-derived label so a blank publisher name still renders
  // an editable price label (empty only for donation/unknown-type prices).
  const label = price.name || defaultPriceLabel(price);
  // One badge per card, sharing the head slot: the trial badge wins, so the
  // promo badge only shows on a non-trial price. (The backend also force-clears
  // badgeLabel when a trial is set; this also covers the preview's mid-edit state.)
  const promo = trial ? '' : (price.badgeLabel ?? '').trim();

  return (
    // The whole card is the radio: clicking (or Space/Enter when focused)
    // highlights it, and the shared CTA below acts on the highlighted price.
    <div
      class="sm-plan-option"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={0}
      onClick={() => {
        if (disabled) {
          return;
        }
        onSelect();
      }}
      onKeyDown={(e) => {
        if (disabled) {
          return;
        }
        // Don't hijack Space/Enter while an inline-edit field inside the card
        // (e.g. the price label the preview makes contenteditable) is focused —
        // let the editor insert the space instead of preventing it and
        // re-selecting the card.
        if ((e.target as HTMLElement).closest?.('[contenteditable="true"]')) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Head line: the price label leads on the left; the single badge (trial,
          else promo) is pushed to the far right. Rendered whenever there's a
          label, blurb, or badge so the price row below always has a header to
          sit under. */}
      {(label || price.description || trial || promo) && (
        <div class="sm-plan-option-head">
          {(label || price.description) && (
            <div class="sm-plan-option-copy">
              {label && <span class="sm-plan-option-name" data-sm-edit="priceName" data-sm-uid={price.externalId}>{label}</span>}
              {price.description && <p class="sm-plan-option-desc">{price.description}</p>}
            </div>
          )}
          {(trial || promo) && (
            <div class="sm-plan-badge-row">
              {/* Clicking the trial badge opens the preview's trial editor popover
                  (length + wording); the data-sm-edit anchors are inert on live sites. */}
              {trial
                ? <TrialBadge days={days} uid={price.externalId} template={trialBadgeTemplate(price.trialBadgeLabel)} />
                : <PromoBadge label={promo} uid={price.externalId} />}
            </div>
          )}
        </div>
      )}
      {/* First-charge-only discounts omit the interval. The banner above
          the list states the discount's duration. */}
      <div class="sm-plan-price">
        {discount && <s class="sm-plan-amount-original">{formatAmount(price)}</s>}
        <span class="sm-plan-amount" data-sm-edit="amount" data-sm-uid={price.externalId}>
          {discount ? formatMoney(discount.discountedAmountCents, discount.currency) : formatAmount(price)}
        </span>
        {showInterval && <span class="sm-plan-interval">{interval}</span>}
      </div>
    </div>
  );
}

// Suggested amounts for a variable donation. Publisher-configured presets win
// when present (sanitized against this price); otherwise they are derived from
// the minimum (min, 2×, 5×, 10×) and clamped to the maximum when one is set —
// the fallback for donations enabled before presets shipped. The minimum is
// always sellable (backend guarantees max >= min), so at least one preset always
// survives.
//
// The derived multiplier set (min, 2×, 5×, 10×) MUST stay in lockstep with the
// dashboard's derivedPresetCents (publisher-frontend .../subscriptionsmanager/
// money.js), which prefills a new donation with the same amounts this fallback
// renders for legacy donations.
function buildPresets(price: OnsitePrice): number[] {
  const base = price.minAmountCents && price.minAmountCents > 0 ? price.minAmountCents : 500;
  const max = price.maxAmountCents;
  const configured = sanitizeConfiguredPresets(price.presetAmountsCents, base, max);
  if (configured.length > 0) return configured;
  const presets: number[] = [];
  for (const candidate of [base, base * 2, base * 5, base * 10]) {
    if (max != null && candidate > max) continue;
    if (!presets.includes(candidate)) presets.push(candidate);
  }
  if (presets.length === 0) presets.push(base);
  return presets;
}

// sanitizeConfiguredPresets drops anything a visitor could never pick (non-integer,
// non-positive, below the minimum, or above the maximum), de-duplicates, and
// sorts ascending. An empty result means the caller falls back to the derivation,
// so a stale preset below a raised minimum can never render.
function sanitizeConfiguredPresets(configured: number[] | undefined, min: number, max: number | undefined): number[] {
  if (!configured || configured.length === 0) return [];
  const seen = new Set<number>();
  const result: number[] = [];
  for (const cents of configured) {
    if (!Number.isInteger(cents) || cents <= 0) continue;
    if (cents < min) continue;
    if (max != null && cents > max) continue;
    if (seen.has(cents)) continue;
    seen.add(cents);
    result.push(cents);
  }
  result.sort((a, b) => a - b);
  return result;
}

// A publisher-requested preset amount is honored only when it's a positive
// in-range value for this price. Out-of-range requests fall back to the
// default presets so normal picker validation applies.
function seedRequestedCents(requested: number | undefined, min: number, max: number | undefined): number | null {
  if (requested == null || !Number.isInteger(requested) || requested <= 0) return null;
  if (requested < min) return null;
  if (max != null && requested > max) return null;
  return requested;
}

interface DonationAmountPickerProps {
  product: OnsiteProduct;
  price: OnsitePrice;
  onSelectPrice: (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => void;
  initialAmountCents?: number;
  editable?: boolean;
}

function DonationAmountPicker({ product, price, onSelectPrice, initialAmountCents, editable }: DonationAmountPickerProps) {
  useT();
  const presets = buildPresets(price);
  const min = price.minAmountCents ?? 0;
  const max = price.maxAmountCents;

  // Honor a valid publisher-requested amount; matching presets just highlight
  // the chip, others open the custom input prefilled. Otherwise preselect the
  // first preset so the CTA is live without a choice. "Other" reveals a custom
  // input; while it's active customText drives the amount.
  const seed = seedRequestedCents(initialAmountCents, min, max);
  const seedIsPreset = seed != null && presets.includes(seed);
  const [selectedCents, setSelectedCents] = useState<number | null>(seed ?? presets[0] ?? null);
  const [customMode, setCustomMode] = useState(seed != null && !seedIsPreset);
  const [customText, setCustomText] = useState(
    seed != null && !seedIsPreset ? minorToMajorText(seed, price.currency) : '',
  );

  const choosePreset = (cents: number) => {
    setCustomMode(false);
    setSelectedCents(cents);
  };

  const chooseCustom = () => {
    setCustomMode(true);
    setSelectedCents(majorToMinor(customText, price.currency));
  };

  const onCustomInput = (text: string) => {
    setCustomText(text);
    setSelectedCents(majorToMinor(text, price.currency));
  };

  const belowMin = selectedCents != null && selectedCents < min;
  const aboveMax = selectedCents != null && max != null && selectedCents > max;
  const valid = selectedCents != null && selectedCents > 0 && !belowMin && !aboveMax;

  let hint: string | null = null;
  if (customMode && belowMin) {
    hint = t('donation.minimum', { amount: formatMoney(min, price.currency) });
  } else if (customMode && aboveMax && max != null) {
    hint = t('donation.maximum', { amount: formatMoney(max, price.currency) });
  }

  // The donate button's label is a template that may carry an {amount} token the
  // widget fills with the chosen amount (default "Donate {amount}"). Before a
  // valid amount is picked, fill the token with the minimum so it still reads
  // sensibly; a valid selection substitutes the chosen amount.
  const amountForLabel = selectedCents != null && selectedCents > 0 ? selectedCents : min;
  const ctaLabel = donateButtonLabel(price, amountForLabel);

  return (
    // notranslate keeps Google Translate from rewriting the picker's live
    // controls: it swaps text nodes for <font> wrappers, which desyncs Preact's
    // diff so the "Donate {amount}" CTA freezes at its initial value instead of
    // following the chosen amount. Only amounts/chrome live here, so nothing
    // worth translating is lost; the publisher's name/description sit outside it.
    <div class="sm-donate-picker notranslate">
      <div class="sm-donate-presets" role="group" aria-label={t('donation.chooseAmount')}>
        {/* Each preset chip carries an inert data-sm-edit="presetAmount" anchor
            (with its index) so the preview edits the amount in place; the "Other"
            chip has none. */}
        {presets.map((cents, i) => (
          <button
            key={cents}
            type="button"
            class={`sm-donate-chip${!customMode && selectedCents === cents ? ' sm-donate-chip-active' : ''}`}
            aria-pressed={!customMode && selectedCents === cents}
            data-sm-edit="presetAmount"
            data-sm-uid={price.externalId}
            data-sm-preset-index={i}
            data-sm-preset-cents={cents}
            data-sm-preset-currency={price.currency}
            onClick={() => choosePreset(cents)}
          >
            {formatMoney(cents, price.currency)}
          </button>
        ))}
        <button
          type="button"
          class={`sm-donate-chip${customMode ? ' sm-donate-chip-active' : ''}`}
          aria-pressed={customMode}
          onClick={chooseCustom}
        >
          {t('donation.other')}
        </button>
      </div>

      {customMode && (
        <label class="sm-field sm-donate-custom">
          <span>{t('donation.enterAmount')}</span>
          <input
            type="number"
            inputMode="decimal"
            class="sm-input"
            min={minorToMajorText(min, price.currency)}
            step={isZeroDecimal(price.currency) ? '1' : '0.01'}
            placeholder={formatMoney(min, price.currency)}
            value={customText}
            onInput={(e) => onCustomInput((e.target as HTMLInputElement).value)}
            aria-label={t('donation.customAmountAria')}
            autofocus
          />
        </label>
      )}

      {hint && <p class="sm-donate-hint" role="alert">{hint}</p>}

      {/* The purchase button carries an inert data-sm-edit="ctaLabel" anchor so
          the preview edits its label template (the {amount} token stays dynamic). */}
      <button
        type="button"
        class="sm-btn sm-btn-primary sm-donate-submit"
        data-sm-edit="ctaLabel"
        data-sm-uid={price.externalId}
        disabled={!editable && !valid}
        onClick={() => valid && onSelectPrice(product, price, selectedCents ?? undefined)}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

interface PaywallTrustProps {
  products: OnsiteProduct[];
  checkoutText?: CheckoutText;
}

function PaywallTrust({ products, checkoutText }: PaywallTrustProps) {
  useT();
  const recurring = products.some((p) => (p.prices ?? []).some((price) => Boolean(price.billingInterval)));
  // A paywall resolves a single product, so its per-product checkout override
  // (when set) wins; each string then falls back to the domain default and
  // finally the computed default, independently (mirrors the header framing).
  const lead = products.find((p) => p.type !== 'donation') ?? products[0];
  const secureCheckoutNote = lead?.checkoutText?.secureCheckoutNote || checkoutText?.secureCheckoutNote || t('trust.secureCheckout');
  const cancelAnytimeLabel = lead?.checkoutText?.cancelAnytimeLabel || checkoutText?.cancelAnytimeLabel || t('trust.cancelAnytime');

  return (
    <div class="sm-trust">
      <span class="sm-trust-item">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {/* data-sm-edit tags the dashboard preview's inline-edit targets. Inert on
            live sites — only the preview bundle wires them (see widget/src/preview.tsx). */}
        <span data-sm-edit="secureCheckoutNote">{secureCheckoutNote}</span>
      </span>
      {recurring && (
        <span class="sm-trust-cancel">
          <span aria-hidden="true">·</span>
          {/* Links to the portal so the "cancel anytime" promise is actionable:
              an existing subscriber seeing the wall can reach their account, and
              it discloses where cancellation happens before they subscribe. */}
          <a class="sm-trust-link" href={getPortalBase()} target="_blank" rel="noopener noreferrer" data-sm-edit="cancelAnytimeLabel">
            {cancelAnytimeLabel}
          </a>
        </span>
      )}
    </div>
  );
}
